import React, { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useSelector } from 'react-redux';
import { apiClient } from '../commonApis/apiClient';
import toast from 'react-hot-toast';
import { useLanguage } from '../context/LanguageContext';
import { 
  IconDownload, 
  IconX, 
  IconSearch, 
  IconFileDescription, 
  IconFileZip,
  IconPlus
} from '@tabler/icons-react';

interface Message {
  id: string;
  senderId: string;
  senderType: string;
  message?: string;
  fileUrl?: string;
  fileType?: string;
  voice?: boolean;
  duration?: string;
  createdAt: string;
}

interface ChatRoom {
  id: string;
  type: 'DIRECT' | 'GROUP';
  name?: string;
  messages: Message[];
  participants: any[];
}

interface Contact {
  id: string;
  name: string;
  type: 'SUPERADMIN' | 'STAFF' | 'ADMIN' | 'LMCLIENT';
}

// ─── Icon helpers (inline SVG, no extra deps) ─────────────────────────────────
const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);
const MicIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
    <line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
  </svg>
);
const GridIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
  </svg>
);
const PaperclipIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);
const EmojiIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <line x1="9" y1="9" x2="9.01" y2="9" />
    <line x1="15" y1="9" x2="15.01" y2="9" />
  </svg>
);
const FileIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <polyline points="13 2 13 9 20 9" />
  </svg>
);
const PlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);
const ChatIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);
const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const BackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

// ─── Quick reply suggestions relevant to admin ↔ superadmin chat ──────────────
const QUICK_REPLIES = [
  { icon: '📦', text: 'Where is my order?' },
  { icon: '🔑', text: 'I need help with my account' },
  { icon: '💳', text: 'Subscription or billing question' },
  { icon: '🚚', text: 'Fleet or delivery issue' },
];

export default function FloatingChatButton() {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [activeRoom, setActiveRoom] = useState<ChatRoom | null>(null);
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [view, setView] = useState<'welcome' | 'chat'>('welcome');
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Multimedia states
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; type: string; name?: string } | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const profile = useSelector((state: any) => state.auth.profile);
  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () =>
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

  // ── Socket ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const baseUrl =
      (process.env.REACT_APP_BASE_URL || '').replace(/\/api\/?$/, '') ||
      'http://localhost:3002';
    socketRef.current = io(baseUrl, { transports: ['websocket'] });

    socketRef.current.on('new-chat-message', (msg: Message) => {
      setMessages((prev) => [...prev, msg]);
      scrollToBottom();
      if (!isOpen) setUnreadCount((c) => c + 1);
    });

    return () => { socketRef.current?.disconnect(); };
  }, []);

  // ── Fetch when opened ───────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && profile?.id) {
      fetchRooms();
      fetchContacts();
    }
    if (isOpen) setUnreadCount(0);
  }, [isOpen, profile?.id]);

  useEffect(() => {
    if (!activeRoom) return;
    socketRef.current?.emit('join-chat-room', activeRoom.id);
    loadMessages(activeRoom.id);
  }, [activeRoom]);

  const fetchRooms = async () => {
    try {
      const res = await apiClient.get<any>(`/api/chat/my-chats/${profile.id}`);
      if (res.success) setRooms((res as any).rooms || []);
    } catch (e) { console.error(e); }
  };

  const fetchContacts = async () => {
    try {
      const res = await apiClient.get<any>('/api/chat/contacts/superadmin');
      if (res.success) {
        const { superAdmins = [], staffs = [] } = (res as any).contacts || {};
        setContacts([
          ...superAdmins.map((s: any) => ({ id: s.id, name: s.name || 'Gwarm Support', type: 'SUPERADMIN' as const })),
          ...staffs.map((s: any) => ({ id: s.id, name: s.name || 'Support Staff', type: 'STAFF' as const })),
        ]);
      }
    } catch (e) { console.error(e); }
  };

  const loadMessages = async (roomId: string) => {
    try {
      const res = await apiClient.get<any>(`/api/chat/room/${roomId}/messages`);
      if (res.success) { setMessages((res as any).messages || []); scrollToBottom(); }
    } catch (e) { console.error(e); }
  };

  const startChat = async (prefillMsg?: string) => {
    if (!profile?.id || contacts.length === 0) return;
    setIsLoading(true);
    try {
      const target = contacts[0];
      const res = await apiClient.post<any>('/api/chat/direct', {
        participantAId: profile.id,
        participantAType: 'ADMIN',
        participantBId: target.id,
        participantBType: target.type,
        clientId: profile.id,
      });
      if (res.success && (res as any).room) {
        setActiveRoom((res as any).room);
        setView('chat');
        await fetchRooms();
        if (prefillMsg) {
          setNewMessage(prefillMsg);
          setTimeout(() => inputRef.current?.focus(), 300);
        }
      }
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  };

  const handleSend = (multimedia?: { fileUrl: string; fileType: string; voice?: boolean; duration?: string }) => {
    if ((!newMessage.trim() && !multimedia) || !activeRoom || !profile?.id) return;
    socketRef.current?.emit('send-chat-message', {
      roomId: activeRoom.id,
      senderId: profile.id,
      senderType: 'ADMIN',
      message: newMessage.trim(),
      ...multimedia
    });
    setNewMessage('');
    setShowEmojiPicker(false);
  };

  // ── Multimedia Handlers ───────────────────────────────────────────────────
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeRoom) return;

    // Check file size (2 MB limit)
    const MAX_SIZE = 2 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error('File size exceeds 2MB limit');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await apiClient.post<any>('/api/chat/upload', formData, true);
      if (res.success) {
        handleSend({
          fileUrl: (res as any).fileUrl,
          fileType: (res as any).fileType
        });
      }
    } catch (err) {
      console.error('File upload failed:', err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const durationStr = formatTime(recordingTime);
        
        const formData = new FormData();
        formData.append('file', audioBlob, 'voice-message.wav');

        setIsUploading(true);
        try {
          const res = await apiClient.post<any>('/api/chat/upload', formData, true);
          if (res.success) {
            handleSend({
              fileUrl: (res as any).fileUrl,
              fileType: 'audio/wav',
              voice: true,
              duration: durationStr
            });
          }
        } catch (err) {
          console.error('Voice upload failed:', err);
        } finally {
          setIsUploading(false);
        }
        
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Microphone access denied:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const addEmoji = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSend();
  };

  const handleQuickReply = (text: string) => {
    if (activeRoom) {
      setNewMessage(text);
      setView('chat');
      setTimeout(() => inputRef.current?.focus(), 200);
    } else {
      startChat(text);
    }
  };

  const openRoom = (room: ChatRoom) => { setActiveRoom(room); setView('chat'); };

  const getRoomLabel = (room: ChatRoom) => {
    if (room.type === 'GROUP') return room.name || 'Group Chat';
    const other = room.participants?.find((p) => p.participantId !== profile?.id);
    return (other?.name && other.name !== 'Unknown') ? other.name : 'Gwarm Support';
  };

  const fmt = (iso: string) =>
    new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const renderFilePreviewModal = () => {
    if (!previewFile) return null;

    const isImage = previewFile.type?.startsWith('image/');
    const isVideo = previewFile.type?.startsWith('video/');
    const isPdf = previewFile.type === 'application/pdf';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="absolute top-4 right-4 flex items-center gap-3">
          <a
            href={previewFile.url}
            download
            className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors backdrop-blur-md"
            title="Download"
            onClick={(e) => e.stopPropagation()}
          >
            <IconDownload size={20} />
          </a>
          <button
            onClick={() => setPreviewFile(null)}
            className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors backdrop-blur-md"
            title="Close"
          >
            <IconX size={20} />
          </button>
        </div>

        <div className="w-full h-full flex items-center justify-center pointer-events-none" onClick={() => setPreviewFile(null)}>
          <div className="max-w-full max-h-full flex items-center justify-center pointer-events-auto" onClick={(e) => e.stopPropagation()}>
            {isImage ? (
              <img
                src={previewFile.url}
                alt="Preview"
                className="max-w-full max-h-[80vh] rounded-lg shadow-2xl object-contain transition-transform duration-300 hover:scale-[1.02]"
              />
            ) : isVideo ? (
              <video
                src={previewFile.url}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] rounded-lg shadow-2xl"
              />
            ) : isPdf ? (
              <iframe
                src={previewFile.url}
                className="w-[85vw] h-[75vh] rounded-lg bg-white shadow-2xl border-none"
                title="PDF Preview"
              />
            ) : (
              <div className="bg-white p-8 rounded-2xl flex flex-col items-center gap-4 shadow-2xl text-center min-w-[280px]">
                <div className="w-16 h-16 bg-gray-50 rounded-xl flex items-center justify-center text-gray-400">
                  <IconFileDescription size={40} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Preview Unavailable</h3>
                  <p className="text-xs text-gray-500">Download the file to view it.</p>
                </div>
                <a
                  href={previewFile.url}
                  download
                  className="w-full py-2.5 bg-[#FBD037] hover:bg-[#f5c800] text-black font-bold rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <IconDownload size={18} />
                  Download
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Chat Dialog ─────────────────────────────────────────────────────── */}
      <div
        className={`fixed bottom-24 left-6 z-50 w-[360px] flex flex-col rounded-2xl overflow-hidden border border-[#e5e5e5] shadow-2xl
          transition-all duration-300 ease-out origin-bottom-right
          ${isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4 pointer-events-none'}`}
        style={{ height: '560px', background: '#FFFFFF' }}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 flex-shrink-0"
          style={{ background: '#212121' }}>
          <div className="flex items-center gap-3">
            {/* Logo / avatar */}
            <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden border-2 border-[#FBD037]/40">
              <img
                src="/img/gwarm-express-logo.svg"
                alt="Gwarm"
                className="w-full h-full object-contain p-1 bg-[#FBD037]"
                onError={(e) => {
                  const el = e.target as HTMLImageElement;
                  el.style.display = 'none';
                  el.parentElement!.innerHTML =
                    `<div style="width:100%;height:100%;background:#FBD037;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;color:#212121">G</div>`;
                }}
              />
            </div>
            <div>
              <div className="text-white font-semibold text-sm leading-tight">{t("gwarm support")}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                <span className="text-white/50 text-[11px]">{t("support team • online")}</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-7 h-7 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Yellow accent bar */}
        <div className="h-[3px] flex-shrink-0" style={{ background: '#FBD037' }} />

        {/* Body */}
        <div className="flex-1 flex flex-col overflow-hidden">

          {/* ── WELCOME VIEW ──────────────────────────────────────────────── */}
          {view === 'welcome' && (
            <div className="flex-1 overflow-y-auto">
              {/* Hero */}
              <div className="px-4 pt-4 pb-3" style={{ background: '#F7F6F2' }}>
                <p className="text-sm font-semibold text-[#212121] mb-0.5">
                  {t("👋 hello! how can we help you?")}
                </p>
                <p className="text-xs text-[#212121]/60 leading-relaxed">
                  {t("our support team is ready to assist with your shipments, account, billing, or any platform question.")}
                </p>
              </div>

              <div className="px-4 py-3 space-y-3">
                {/* Quick replies */}
                <div>
                  <p className="text-[10px] font-semibold text-[#212121]/40 uppercase tracking-widest mb-2">
                    {t("common questions")}
                  </p>
                  <div className="space-y-2">
                    {QUICK_REPLIES.map((q) => (
                      <button
                        key={q.text}
                        onClick={() => handleQuickReply(language === 'ar' ? t(q.text.toLowerCase()) : q.text)}
                        className="w-full text-left px-3 py-2.5 rounded-xl border border-[#e5e5e5] bg-white hover:border-[#FBD037] hover:bg-[#FFFBEB] text-sm text-[#212121] font-medium transition-all flex items-center gap-2.5 group"
                      >
                        <span className="text-base leading-none">{q.icon}</span>
                        <span className="group-hover:text-[#b45309] transition-colors">{language === 'ar' ? t(q.text.toLowerCase()) : q.text}</span>
                        <svg className="ml-auto opacity-30 group-hover:opacity-60" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Previous conversations */}
                {rooms.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-[#212121]/40 uppercase tracking-widest mb-2">
                      {t("previous conversations")}
                    </p>
                    <div className="space-y-2">
                      {rooms.slice(0, 3).map((room) => {
                        const lastMsg = room.messages?.[room.messages.length - 1];
                        return (
                          <button
                            key={room.id}
                            onClick={() => openRoom(room)}
                            className="w-full text-left px-3 py-2.5 rounded-xl bg-[#F7F6F2] hover:bg-[#f0ede4] transition-colors flex items-center gap-3 border border-transparent hover:border-[#e5e5e5]"
                          >
                            <div className="w-8 h-8 rounded-full bg-[#FBD037] flex items-center justify-center text-[#212121] font-bold text-xs flex-shrink-0">
                              {getRoomLabel(room).charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-[#212121] truncate">{getRoomLabel(room)}</p>
                              <p className="text-[10px] text-[#212121]/50 truncate">
                                {lastMsg?.message || t('no messages yet')}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Start new */}
                <button
                  onClick={() => startChat()}
                  disabled={isLoading || contacts.length === 0}
                  className="w-full py-2.5 rounded-xl text-[#212121] text-sm font-semibold bg-[#FBD037] hover:bg-[#f5c800] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm"
                >
                  {isLoading
                    ? <span className="w-4 h-4 border-2 border-[#212121]/30 border-t-[#212121] rounded-full animate-spin" />
                    : <ChatIcon />
                  }
                  {t("start a conversation")}
                </button>
              </div>
            </div>
          )}

          {/* ── CHAT VIEW ─────────────────────────────────────────────────── */}
          {view === 'chat' && (
            <>
              {/* Chat sub-header */}
              <div className="flex items-center gap-2 px-4 py-2 border-b border-[#e5e5e5] flex-shrink-0 bg-white">
                <button
                  onClick={() => { setView('welcome'); setActiveRoom(null); setMessages([]); }}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[#212121]/60 hover:bg-[#F7F6F2] transition-colors"
                >
                  <BackIcon />
                </button>
                <div className="w-6 h-6 rounded-full bg-[#FBD037] flex items-center justify-center font-bold text-[#212121] text-[10px]">
                  {activeRoom ? getRoomLabel(activeRoom).charAt(0).toUpperCase() : 'G'}
                </div>
                <span className="text-sm font-semibold text-[#212121] truncate">
                  {activeRoom ? getRoomLabel(activeRoom) : t('gwarm support')}
                </span>
                <div className="ml-auto flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  <span className="text-[10px] text-[#212121]/40">{t("active")}</span>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: '#F7F6F2' }}>
                {messages.length === 0 && (
                  <div className="flex justify-center">
                    <div className="bg-white rounded-xl px-4 py-2.5 text-xs text-[#212121]/50 border border-[#e5e5e5] shadow-sm">
                      {t("send a message to get started 👇")}
                    </div>
                  </div>
                )}

                {messages.map((msg) => {
                  const isMe = msg.senderId === profile?.id;
                  return (
                    <div key={msg.id} className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                      {!isMe && (
                        <div className="w-6 h-6 rounded-full bg-[#FBD037] flex items-center justify-center text-[#212121] font-bold text-[9px] flex-shrink-0 mb-0.5">
                          SA
                        </div>
                      )}
                        <div className={`max-w-[78%] px-3 py-2 text-sm break-words ${
                           isMe
                             ? 'bg-[#212121] text-white rounded-l-2xl rounded-tr-2xl'
                             : 'bg-white text-[#212121] rounded-r-2xl rounded-tl-2xl border border-[#e5e5e5]'
                         }`}>
                           {msg.message && <p className="leading-relaxed text-[13px]">{msg.message}</p>}
                           
                           {msg.fileUrl && !msg.voice && (
                             <div className="mt-1">
                               {msg.fileType?.startsWith('image/') ? (
                                 <div 
                                   className="relative group cursor-pointer overflow-hidden rounded-lg"
                                   onClick={() => setPreviewFile({ url: msg.fileUrl!, type: msg.fileType! })}
                                 >
                                   <img
                                     src={msg.fileUrl}
                                     alt="attachment"
                                     className="max-w-full h-auto border border-white/10 transition-transform duration-300 group-hover:scale-105"
                                   />
                                   <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                     <IconSearch size={20} className="text-white" />
                                   </div>
                                 </div>
                               ) : (
                                 <div
                                   onClick={() => setPreviewFile({ url: msg.fileUrl!, type: msg.fileType! })}
                                   className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                                     isMe ? 'bg-white/10 border-white/20 hover:bg-white/20' : 'bg-gray-100 border-gray-200 hover:bg-gray-200'
                                   } no-underline`}
                                 >
                                   <div className={isMe ? 'text-[#FBD037]' : 'text-[#212121]'}>
                                     {msg.fileType?.includes('zip') ? <IconFileZip size={18} /> : <IconFileDescription size={18} />}
                                   </div>
                                   <div className="flex-1 min-w-0">
                                     <p className={`text-[11px] font-medium truncate ${isMe ? 'text-white' : 'text-[#212121]'}`}>{t("attachment")}</p>
                                     <p className={`text-[9px] ${isMe ? 'text-white/40' : 'text-[#212121]/40'}`}>{t("click to preview")}</p>
                                   </div>
                                 </div>
                               )}
                             </div>
                           )}

                          {msg.voice && msg.fileUrl && (
                            <div className={`mt-1 flex items-center gap-2 p-2 rounded-xl ${isMe ? 'bg-white/10' : 'bg-gray-100'}`}>
                              <button 
                                onClick={(e) => {
                                  const audio = e.currentTarget.parentElement?.querySelector('audio');
                                  if (audio) {
                                    if (audio.paused) {
                                      document.querySelectorAll('audio').forEach(a => { if(a !== audio) a.pause() });
                                      audio.play();
                                    } else {
                                      audio.pause();
                                    }
                                  }
                                }}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${isMe ? 'bg-[#FBD037] text-[#212121]' : 'bg-[#212121] text-white shadow-sm'} hover:scale-105 active:scale-95`}
                              >
                                <PlayIcon />
                              </button>
                              <div className="flex-1">
                                <div className={`h-1 rounded-full ${isMe ? 'bg-white/20' : 'bg-gray-300'} relative overflow-hidden`}>
                                  <div className="absolute left-0 top-0 h-full w-0 bg-current rounded-full transition-all duration-300 audio-progress" />
                                </div>
                                <div className="flex justify-between mt-1">
                                  <span className="text-[9px] opacity-60">{t("voice message")}</span>
                                  <span className="text-[9px] opacity-60">{msg.duration || '0:00'}</span>
                                </div>
                              </div>
                              <audio 
                                src={msg.fileUrl} 
                                className="hidden" 
                                onTimeUpdate={(e) => {
                                  const audio = e.currentTarget;
                                  const progress = audio.parentElement?.querySelector('.audio-progress') as HTMLElement;
                                  if (progress) {
                                    const percent = (audio.currentTime / audio.duration) * 100;
                                    progress.style.width = `${percent}%`;
                                  }
                                }}
                                onEnded={(e) => {
                                  const progress = e.currentTarget.parentElement?.querySelector('.audio-progress') as HTMLElement;
                                  if (progress) progress.style.width = '0%';
                                }}
                              />
                            </div>
                          )}

                          <p className={`text-[10px] mt-1 text-right ${isMe ? 'text-white/40' : 'text-[#212121]/30'}`}>
                            {fmt(msg.createdAt)}
                          </p>
                        </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input bar */}
              <div className="relative flex items-center gap-2 px-3 py-2.5 border-t border-[#e5e5e5] flex-shrink-0 bg-white">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileUpload}
                />
                
                {showEmojiPicker && (
                  <div className="absolute bottom-full left-3 mb-2 p-2 bg-white rounded-2xl shadow-2xl border border-[#e5e5e5] grid grid-cols-6 gap-1 z-10 animate-in fade-in slide-in-from-bottom-2">
                    {['😊', '😂', '😍', '👍', '🙏', '🔥', '👏', '🙌', '⭐', '📍', '✅', '❌'].map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => addEmoji(emoji)}
                        className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg text-lg transition-colors"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}

                <button 
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${showEmojiPicker ? 'text-[#FBD037] bg-[#212121]' : 'text-[#212121]/40 hover:bg-[#F7F6F2]'}`}
                >
                  <EmojiIcon />
                </button>
                
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-[#212121]/40 hover:bg-[#F7F6F2] transition-colors flex-shrink-0"
                >
                  {isUploading ? (
                    <span className="w-4 h-4 border-2 border-[#212121]/20 border-t-[#212121] rounded-full animate-spin" />
                  ) : (
                    <PaperclipIcon />
                  )}
                </button>

                {isRecording ? (
                  <div className="flex-1 flex items-center gap-3 px-3 py-2 rounded-full bg-red-50 border border-red-100">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="flex-1 text-xs font-medium text-red-600">{t("recording...")} {formatTime(recordingTime)}</span>
                    <button onClick={stopRecording} className="text-[10px] font-bold text-red-600 uppercase tracking-wider hover:underline">{t("stop")}</button>
                  </div>
                ) : (
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder={t("type your message here...")}
                    className="flex-1 text-sm px-3 py-2 rounded-full border border-[#e5e5e5] outline-none focus:border-[#FBD037] bg-[#F7F6F2] transition-colors placeholder:text-[#212121]/30"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKey}
                  />
                )}

                {!isRecording && (
                  <button 
                    onClick={startRecording}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[#212121]/40 hover:bg-[#F7F6F2] transition-colors flex-shrink-0"
                  >
                    <MicIcon />
                  </button>
                )}

                <button
                  onClick={() => handleSend()}
                  disabled={(!newMessage.trim() && !isRecording) || isUploading}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-[#212121] bg-[#FBD037] hover:bg-[#f5c800] transition-all disabled:opacity-30 flex-shrink-0 shadow-sm active:scale-95"
                >
                  <SendIcon />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="py-2 text-center bg-white border-t border-[#e5e5e5] flex-shrink-0">
          <span className="text-[10px] text-[#212121]/30">
            {t("powered by")} <span className="font-semibold text-[#212121]/50">Gwarm</span>
          </span>
        </div>
      </div>

      {renderFilePreviewModal()}

      {/* ── FAB button ──────────────────────────────────────────────────────── */}
      <button
        id="floating-chat-btn"
        onClick={() => setIsOpen((o) => !o)}
        className="fixed bottom-6 left-6 z-20 w-14 h-14 rounded-full flex items-center justify-center shadow-xl text-[#212121]
          transition-all duration-300 hover:scale-110 active:scale-95"
        style={{ background: '#FBD037' }}
        title={t("chat with gwarm support")}
      >
        <div className={`transition-all duration-200 ${isOpen ? 'rotate-0 scale-110' : 'rotate-0'}`}>
          {isOpen ? <CloseIcon /> : <ChatIcon />}
        </div>

        {/* Unread badge */}
        {unreadCount > 0 && !isOpen && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}

        {/* Pulse ring */}
        {!isOpen && (
          <span className="absolute inset-0 rounded-full animate-ping opacity-25 bg-[#FBD037]" />
        )}
      </button>
    </>
  );
}
